/**
 * Theme Resolver
 * Manages theme resolution and switching
 */

import type { Theme, IThemeResolver } from '@atlas-forms/types-js';
import { lightTheme } from './themes/light-theme';
import { darkTheme } from './themes/dark-theme';

/**
 * Theme resolver implementation
 * Manages light and dark themes with system preference detection
 */
export class ThemeResolver implements IThemeResolver {
  private themes: Map<string, Theme>;
  private currentTheme: Theme;
  private listeners: Set<(theme: Theme) => void>;

  constructor(customThemes?: { light?: Theme; dark?: Theme }) {
    this.themes = new Map();
    this.listeners = new Set();

    // Register default themes
    this.themes.set('light', customThemes?.light || lightTheme);
    this.themes.set('dark', customThemes?.dark || darkTheme);

    // Set default theme based on system preference
    this.currentTheme = this.getSystemTheme();
  }

  /**
   * Get the current theme
   */
  getCurrent(): Theme {
    return this.currentTheme;
  }

  /**
   * Resolve theme by name
   */
  resolve(themeName: string): Theme {
    const theme = this.themes.get(themeName);
    if (!theme) {
      console.warn(`Theme '${themeName}' not found, using current theme`);
      return this.currentTheme;
    }
    return theme;
  }

  /**
   * Set the current theme
   */
  setTheme(themeName: string): void {
    const theme = this.themes.get(themeName);
    if (!theme) {
      throw new Error(`Theme '${themeName}' not found`);
    }
    this.currentTheme = theme;
    this.notifyListeners(theme);
  }

  /**
   * Toggle between light and dark themes
   */
  toggle(): void {
    const nextTheme = this.currentTheme.name === 'light' ? 'dark' : 'light';
    this.setTheme(nextTheme);
  }

  /**
   * Register a custom theme
   */
  register(name: string, theme: Theme): void {
    this.themes.set(name, theme);
  }

  /**
   * Get all available themes
   */
  getAvailable(): string[] {
    return Array.from(this.themes.keys());
  }

  // IThemeResolver interface methods
  getTheme(name: string): Theme | undefined {
    return this.themes.get(name);
  }

  getColorScheme(theme: Theme | string): import('@atlas-forms/types-js').ColorScheme {
    const t = typeof theme === 'string' ? this.themes.get(theme) : theme;
    return (t || this.currentTheme).colors;
  }

  getComponentStyles(_theme: Theme, _component: string): Record<string, unknown> {
    return {};
  }

  registerTheme(name: string, theme: Theme): void {
    this.themes.set(name, theme);
  }

  getAvailableThemes(): string[] {
    return Array.from(this.themes.keys());
  }

  /**
   * Subscribe to theme changes
   */
  onChange(listener: (theme: Theme) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Get system theme preference
   * @returns 'light' or 'dark' based on system preference
   */
  private getSystemTheme(): Theme {
    if (typeof window === 'undefined') {
      return this.themes.get('light') || lightTheme;
    }

    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const themeName = prefersDark ? 'dark' : 'light';
    return this.themes.get(themeName) || lightTheme;
  }

  /**
   * Listen to system theme changes
   */
  watchSystemPreference(): void {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', (e) => {
      const themeName = e.matches ? 'dark' : 'light';
      this.setTheme(themeName);
    });
  }

  /**
   * Notify all listeners of theme change
   */
  private notifyListeners(theme: Theme): void {
    for (const listener of this.listeners) {
      try {
        listener(theme);
      } catch (error) {
        console.error('Theme listener error:', error);
      }
    }
  }
}

/**
 * Create a default theme resolver with standard themes
 */
export function createDefaultThemeResolver(): ThemeResolver {
  return new ThemeResolver();
}

/**
 * Global theme resolver singleton
 */
let globalResolver: ThemeResolver | null = null;

/**
 * Get the global theme resolver
 */
export function getGlobalThemeResolver(): ThemeResolver {
  if (!globalResolver) {
    globalResolver = createDefaultThemeResolver();
  }
  return globalResolver;
}

/**
 * Reset the global theme resolver (for testing)
 */
export function resetGlobalThemeResolver(): void {
  globalResolver = null;
}
